/**
 * PasswordValidator - Real-time password strength validation
 *
 * Extracted from signup.html inline scripts.
 * Provides visual feedback on password strength.
 */

export class PasswordValidator {
  /**
   * @param {object} options
   * @param {string|Element} options.input - Password input element
   * @param {string|Element} [options.strengthIndicator] - Strength indicator element
   * @param {string|Element} [options.confirmInput] - Confirm password input
   * @param {string|Element} [options.matchIndicator] - Match indicator element
   * @param {number} [options.minLength=12] - Minimum password length
   */
  constructor(options) {
    this.input = typeof options.input === 'string'
      ? document.querySelector(options.input)
      : options.input;

    this.strengthIndicator = options.strengthIndicator
      ? (typeof options.strengthIndicator === 'string'
        ? document.querySelector(options.strengthIndicator)
        : options.strengthIndicator)
      : null;

    this.confirmInput = options.confirmInput
      ? (typeof options.confirmInput === 'string'
        ? document.querySelector(options.confirmInput)
        : options.confirmInput)
      : null;

    this.matchIndicator = options.matchIndicator
      ? (typeof options.matchIndicator === 'string'
        ? document.querySelector(options.matchIndicator)
        : options.matchIndicator)
      : null;

    this.minLength = options.minLength || 12;
    this.onStrengthChange = options.onStrengthChange || (() => {});

    this.init();
  }

  /**
   * Initialize event listeners
   */
  init() {
    if (this.input) {
      this.input.addEventListener('input', () => this.checkStrength());
      this.input.addEventListener('blur', () => this.checkStrength());
    }

    if (this.confirmInput) {
      this.confirmInput.addEventListener('input', () => this.checkMatch());
      this.input?.addEventListener('input', () => this.checkMatch());
    }
  }

  /**
   * Check password strength
   * @returns {object} - { score, level, feedback }
   */
  checkStrength() {
    const password = this.input?.value || '';
    const result = this.calculateStrength(password);

    this.updateStrengthIndicator(result);
    this.onStrengthChange(result);

    return result;
  }

  /**
   * Calculate password strength
   * @param {string} password
   * @returns {object}
   */
  calculateStrength(password) {
    let score = 0;
    const feedback = [];

    // Length check
    if (password.length >= this.minLength) {
      score += 25;
    } else {
      feedback.push(`At least ${this.minLength} characters required`);
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 15;
    } else {
      feedback.push('Add lowercase letters');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 20;
    } else {
      feedback.push('Add uppercase letters');
    }

    // Number check
    if (/\d/.test(password)) {
      score += 20;
    } else {
      feedback.push('Add numbers');
    }

    // Special character check
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      score += 20;
    } else {
      feedback.push('Add special characters');
    }

    // Bonus for extra length
    if (password.length > 16) {
      score += Math.min((password.length - 16) * 2, 10);
    }

    // Cap at 100
    score = Math.min(score, 100);

    // Determine level
    let level;
    if (score < 25) {
      level = 'weak';
    } else if (score < 50) {
      level = 'fair';
    } else if (score < 75) {
      level = 'good';
    } else {
      level = 'strong';
    }

    return { score, level, feedback, isValid: score >= 50 };
  }

  /**
   * Update strength indicator UI
   * @param {object} result
   */
  updateStrengthIndicator(result) {
    if (!this.strengthIndicator) return;

    // Update classes
    this.strengthIndicator.className = `password-strength password-strength--${result.level}`;

    // Update progress bar if present
    const progressBar = this.strengthIndicator.querySelector('.password-strength__bar');
    if (progressBar) {
      progressBar.style.width = `${result.score}%`;
    }

    // Update label if present
    const label = this.strengthIndicator.querySelector('.password-strength__label');
    if (label) {
      const labels = {
        weak: 'Weak',
        fair: 'Fair',
        good: 'Good',
        strong: 'Strong',
      };
      label.textContent = labels[result.level];
    }

    // Update feedback if present
    const feedbackEl = this.strengthIndicator.querySelector('.password-strength__feedback');
    if (feedbackEl && result.feedback.length > 0) {
      feedbackEl.innerHTML = result.feedback.map(f => `<li>${f}</li>`).join('');
    }
  }

  /**
   * Check if passwords match
   * @returns {boolean}
   */
  checkMatch() {
    if (!this.confirmInput || !this.input) return true;

    const password = this.input.value;
    const confirm = this.confirmInput.value;
    const matches = password === confirm && confirm.length > 0;

    this.updateMatchIndicator(matches, confirm.length > 0);

    return matches;
  }

  /**
   * Update match indicator UI
   * @param {boolean} matches
   * @param {boolean} hasValue
   */
  updateMatchIndicator(matches, hasValue) {
    if (!this.matchIndicator) return;

    if (!hasValue) {
      this.matchIndicator.className = 'password-match';
      this.matchIndicator.textContent = '';
    } else if (matches) {
      this.matchIndicator.className = 'password-match password-match--valid';
      this.matchIndicator.textContent = 'Passwords match';
    } else {
      this.matchIndicator.className = 'password-match password-match--invalid';
      this.matchIndicator.textContent = 'Passwords do not match';
    }

    // Update confirm input validation state
    if (this.confirmInput) {
      this.confirmInput.classList.toggle('is-valid', matches && hasValue);
      this.confirmInput.classList.toggle('is-invalid', !matches && hasValue);
    }
  }

  /**
   * Validate password meets requirements
   * @returns {boolean}
   */
  isValid() {
    const strength = this.checkStrength();
    const matches = this.checkMatch();
    return strength.isValid && matches;
  }
}

export default PasswordValidator;
